import React from 'react';
import { useIntl } from 'react-intl';
import { BsCalendarDay } from 'react-icons/bs';
import { Button, Badge } from 'reactstrap';
import { TaskOccurrenceCard } from '../../taskTemplates/components/TaskOccurrenceCard';
import type { TaskOccurrence } from '../../../core/api/rtk/taskTemplatesApi';

interface TaskSource {
  id: number;
  name: string;
  kind: 'group' | 'item';
}

interface MappedDailyTask {
  id: string;
  name: string;
  raw?: TaskOccurrence | null;
  source?: TaskSource | null;
}

type SourceSelectHandler = (
  source: { id: number; kind: 'group' | 'item' } | null | undefined
) => void;

interface DailyTasksSectionProps {
  tasks: MappedDailyTask[];
  onAdd?: () => void;
  onOpenOccurrence?: (occurrence: TaskOccurrence) => void;
  onArchive?: (task: MappedDailyTask) => void;
  showSource?: boolean;
  hideSourceName?: string;
  onSelectSource?: SourceSelectHandler;
}

const SOURCE_KIND_LABEL: Record<TaskSource['kind'], string> = {
  group: 'Group',
  item: 'Sub-Asset',
};

export const renderSourceChip = (
  source?: TaskSource | null,
  hideSourceName?: string,
  onSelectSource?: SourceSelectHandler
): React.ReactNode => {
  if (!source?.name) return null;
  if (hideSourceName && source.name === hideSourceName) return null;
  const kindLabel = SOURCE_KIND_LABEL[source.kind];
  const clickable = typeof onSelectSource === 'function';
  const handleActivate = (): void => {
    if (!clickable) return;
    onSelectSource?.({ id: source.id, kind: source.kind });
  };
  return (
    <span
      className={`asset-section__source-chip asset-section__source-chip--${source.kind}${
        clickable ? ' asset-section__source-chip--clickable' : ''
      }`}
      title={`${kindLabel}: ${source.name}`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={
        clickable
          ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              handleActivate();
            }
          : undefined
      }
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                handleActivate();
              }
            }
          : undefined
      }
    >
      <span className="asset-section__source-chip-kind">{kindLabel}</span>
      <span className="asset-section__source-chip-name">{source.name}</span>
    </span>
  );
};

const DailyTasksSection: React.FC<DailyTasksSectionProps> = ({
  tasks,
  onAdd,
  onOpenOccurrence,
  onArchive,
  showSource,
  hideSourceName,
  onSelectSource,
}) => {
  const intl = useIntl();

  const rows = tasks.filter((t) => !!t.raw);

  return (
    <div className="asset-section asset-section--daily-tasks">
      <div className="asset-section__header">
        <div className="asset-section__header-left">
          <BsCalendarDay className="asset-section__header-icon" size={16} color="#6c757d" />
          <h3 className="asset-section__title">
            {intl.formatMessage({ id: 'assets.sections.dailyTasks' })}
          </h3>
          <Badge color="primary" pill>
            {rows.length}
          </Badge>
        </div>
        <Button outline color="primary" size="sm" onClick={onAdd}>
          <i className="uil uil-plus mr-1" />
          {intl.formatMessage({ id: 'assets.actions.add' })}
        </Button>
      </div>

      <div className="asset-section__content">
        <div className="tasks-daily-table asset-section__task-list">
          {rows.map((task) => {
            const occ = task.raw as TaskOccurrence;
            const sourceChip = showSource
              ? renderSourceChip(task.source, hideSourceName, onSelectSource)
              : null;
            return (
              <div key={task.id} className="asset-section__task-row">
                <TaskOccurrenceCard
                  occurrence={occ}
                  onOpen={(o) => onOpenOccurrence?.(o)}
                  className="tabular"
                  additionalLines={sourceChip}
                />
                {onArchive && (
                  <button
                    type="button"
                    className="asset-section__row-archive"
                    title={intl.formatMessage({ id: 'archive', defaultMessage: 'Archive' })}
                    onClick={() => onArchive(task)}
                  >
                    <i className="uil uil-archive" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <button className="asset-section__add-inline" onClick={onAdd}>
          <i className="uil uil-plus mr-1" />
          {intl.formatMessage({ id: 'assets.dailyTasks.addInline' })}
        </button>
      </div>
    </div>
  );
};

export default DailyTasksSection;
